import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL, HERO_IMAGE } from "../lib/constants";
import SocialAuthButtons from "../components/SocialAuthButtons";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  function friendlyError(raw) {
    if (raw && /\(404\)|\(50\d\)|red o CORS|contactar al servidor|Network/i.test(raw)) {
      return "No pudimos conectar con el servidor. Asegúrate de estar usando el enlace más reciente de la aplicación e inténtalo de nuevo.";
    }
    return raw;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    const r = await login(email, password);
    setLoading(false);
    if (r.ok) navigate(from, { replace: true });
    else setError(friendlyError(r.error));
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-50" data-testid="login-page">
      {/* Left: brand */}
      <div
        className="hidden lg:flex relative overflow-hidden text-white p-12 flex-col justify-between"
        style={{ background: "linear-gradient(135deg, #0B1E36 0%, #1E3A8A 100%)" }}
      >
        <img
          src={HERO_IMAGE}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0B1E36]/95 via-[#0B1E36]/80 to-[#1E3A8A]/70" />

        <div className="relative">
          {/* Logo removido del panel izquierdo — el logo principal se muestra centrado en el formulario a la derecha */}
        </div>

        <div className="relative">
          <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-brand-gold">
            ConstruLink ID
          </p>
          <h1 className="mt-3 text-4xl xl:text-5xl font-extrabold leading-tight tracking-tight">
            Una identidad.
            <br />
            <span className="text-brand-gold">Cinco soluciones.</span>
            <br />
            Un solo Ecosistema.
          </h1>
          <p className="mt-4 text-slate-300 text-base max-w-md">
            El sistema oficial de acceso al Ecosistema ConstruLink. Inicia sesión una sola vez y accede a toda la plataforma.
          </p>

          <div className="mt-8 flex items-center gap-3 text-xs text-slate-300">
            <div className="flex items-center gap-1.5">
              <ShieldCheck size={14} className="text-brand-gold" /> Autenticación segura
            </div>
            <span className="opacity-40">·</span>
            <div>SSO empresarial</div>
            <span className="opacity-40">·</span>
            <div>JWT + Refresh</div>
          </div>
        </div>

        <div className="relative text-[11px] text-slate-400">
          © {new Date().getFullYear()} ConstruLink · Plataforma Integral para la Industria de la Construcción
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <div className="w-full max-w-md">
          {/* Logo protagonista sobre tarjeta corporativa con contraste y sombra */}
          <div className="mb-8" data-testid="login-logo-block">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-white to-slate-50 ring-1 ring-slate-200/70 shadow-[0_22px_55px_-22px_rgba(11,30,54,0.38)] px-8 py-8 flex items-center justify-center">
              <div aria-hidden className="absolute -top-12 -right-10 w-44 h-44 rounded-full bg-brand-blue/[0.06] blur-2xl" />
              <div aria-hidden className="absolute -bottom-14 -left-10 w-44 h-44 rounded-full bg-brand-red/[0.06] blur-2xl" />
              <div aria-hidden className="absolute inset-x-8 bottom-4 h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" />
              <img
                src={LOGO_URL}
                alt="ConstruLink"
                className="relative w-full max-w-md h-auto object-contain"
              />
            </div>
          </div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red text-center">
            Iniciar sesión
          </p>
          <h2 className="mt-2 text-3xl font-extrabold text-slate-900 tracking-tight text-center">
            Bienvenido de nuevo
          </h2>
          <p className="mt-1 text-slate-500 text-sm text-center">
            Accede al Ecosistema ConstruLink con tu ConstruLink ID.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                Correo electrónico
              </label>
              <input
                type="email"
                autoComplete="email"
                required
                data-testid="login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@empresa.com"
                className="mt-1.5 w-full h-12 px-4 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none text-slate-800"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Contraseña
                </label>
                <Link to="/login" className="text-xs font-semibold text-brand-blue hover:text-brand-red">
                  ¿Olvidaste tu contraseña?
                </Link>
              </div>
              <div className="mt-1.5 relative">
                <input
                  type={showPw ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full h-12 px-4 pr-12 rounded-xl border border-slate-200 bg-white focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none text-slate-800"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800"
                  aria-label="Mostrar contraseña"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <p className="text-sm text-brand-red bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-12 inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red-hover disabled:opacity-60 text-white font-semibold rounded-xl transition-colors shadow-sm"
            >
              {loading ? "Ingresando…" : (<>Iniciar sesión <ArrowRight size={16} /></>)}
            </button>
          </form>

          {/* Bloque destacado: registro de nuevos usuarios (jerarquía secundaria, sin competir con el botón rojo) */}
          <div
            className="mt-5 rounded-2xl border border-brand-blue/20 bg-brand-blue/[0.04] p-4 text-center"
            data-testid="register-highlight"
          >
            <p className="text-sm font-bold text-slate-900">¿Eres nuevo en ConstruLink?</p>
            <p className="mt-1 text-xs text-slate-500">
              Crea gratis tu ConstruLink ID y accede al Ecosistema ConstruLink.
            </p>
            <Link
              to="/register"
              data-testid="link-register"
              className="mt-3 w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl border-2 border-brand-blue text-brand-blue hover:bg-brand-blue hover:text-white font-semibold text-sm transition-colors"
            >
              Crear mi ConstruLink ID <ArrowRight size={16} />
            </Link>
          </div>

          <SocialAuthButtons />
        </div>
      </div>
    </div>
  );
}
